"use client";
import { commentStyles } from "./forum-comments.styles";

import type {
  ForumComposeComment,
  ForumCommentRecord,
  ForumVote,
  ForumCommentVoteHandler,
  ForumMarkdownRenderer,
  ForumLinkRenderer,
  ForumCommentDeletion as CommentDeletion,
} from "../forum.types";
import { useState, useRef, useId } from "react";
import { Button, Alert } from "@softmax/design-system";
import * as stylex from "@stylexjs/stylex";
import { styles } from "../forum.styles";
import { ForumCommentDeletion } from "./forum-comment-deletion";
import { CommentComposer } from "./forum-comment-composer";
import { Byline } from "../forum-byline";
import { VoteButton } from "../forum-vote-button";

export function Comment({
  comment,
  depth,
  votes,
  onVote,
  renderMarkdown,
  compose,
  renderLink,
  deleteComment,
}: Readonly<{
  comment: ForumCommentRecord;
  depth: number;
  votes: Readonly<Record<string, ForumVote>> | undefined;
  onVote: ForumCommentVoteHandler;
  renderMarkdown: ForumMarkdownRenderer;
  compose: ForumComposeComment | undefined;
  renderLink: ForumLinkRenderer;
  deleteComment?: CommentDeletion | undefined;
}>) {
  const [collapsed, setCollapsed] = useState(false);
  const contentId = useId();
  const commentRoot = useRef<HTMLElement>(null);
  const vote = votes?.[comment.id];
  return (
    <article
      ref={commentRoot}
      tabIndex={-1}
      {...stylex.props(commentStyles.comment)}
    >
      <Byline {...comment} renderLink={renderLink} />
      <Button
        size="sm"
        variant="quiet"
        aria-expanded={!collapsed}
        aria-label={`${collapsed ? "Expand" : "Collapse"} ${comment.author.name}’s comment`}
        aria-controls={contentId}
        onClick={() => setCollapsed(!collapsed)}
      >
        {collapsed ? "Expand" : "Collapse"}
      </Button>
      <div id={contentId} hidden={collapsed}>
        <div {...stylex.props(commentStyles.commentBody)}>
          {renderMarkdown({ body: comment.body, headingSlugs: false })}
        </div>
        <div {...stylex.props(commentStyles.commentActions)}>
          <VoteButton
            direction={1}
            label={`${comment.author.name}’s comment`}
            vote={vote}
            onVote={(direction) => onVote({ commentId: comment.id, direction })}
          />
          <span
            aria-label={`${comment.score} points`}
            {...stylex.props(styles.score)}
          >
            {comment.score}
          </span>
          <VoteButton
            direction={-1}
            label={`${comment.author.name}’s comment`}
            vote={vote}
            onVote={(direction) => onVote({ commentId: comment.id, direction })}
          />
        </div>
        {vote?.error && (
          <Alert tone="danger" announcement="assertive">
            {vote.error}
          </Alert>
        )}
        <ForumCommentDeletion
          deletion={deleteComment}
          canDelete={comment.canDelete}
          commentId={comment.id}
          commentRoot={commentRoot}
        />
        {compose && (
          <CommentComposer
            parentId={comment.id}
            label={`Reply to ${comment.author.name}`}
            compose={compose}
          />
        )}
        {comment.replies.length > 0 && (
          <div {...stylex.props(depth < 4 && commentStyles.replies)}>
            {comment.replies.map((reply) => (
              <Comment
                key={reply.id}
                comment={reply}
                depth={depth + 1}
                votes={votes}
                onVote={onVote}
                renderMarkdown={renderMarkdown}
                renderLink={renderLink}
                deleteComment={deleteComment}
                compose={compose}
              />
            ))}
          </div>
        )}
      </div>
    </article>
  );
}
